import { useState, useEffect, useRef, useCallback } from "react"
import {
	watchMessages,
	markAsRead,
	Message,
	Group,
} from "firebase/groupsService"

export function useMessages(selectedGroup: Group | null, loggedUserId: string) {
	const [messages, setMessages] = useState<Message[]>([])
	const [loadingMessages, setLoadingMessages] = useState(false)
	const [showScrollBtn, setShowScrollBtn] = useState(false)
	const [floatingDate, setFloatingDate] = useState<string | null>(null)

	const chatMessagesRef = useRef<HTMLDivElement>(null)
	const messagesEndRef = useRef<HTMLDivElement>(null)
	const msgUnsubRef = useRef<(() => void) | null>(null)

	useEffect(() => {
		if (!selectedGroup) return

		setLoadingMessages(true)
		setMessages([])

		if (msgUnsubRef.current) msgUnsubRef.current()

		const unsub = watchMessages(selectedGroup.id, (msgs) => {
			setMessages(msgs)
			setLoadingMessages(false)
		})
		msgUnsubRef.current = unsub

		if (loggedUserId) markAsRead(selectedGroup.id, loggedUserId)

		return () => unsub()
	}, [selectedGroup?.id, loggedUserId])

	useEffect(() => {
		if (messages.length > 0) scrollToBottom(false)
	}, [messages])

	const scrollToBottom = useCallback((smooth = false) => {
		// Double RAF: first waits for React to commit to DOM,
		// second waits for browser to recalculate layout (scrollHeight)
		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				const el = chatMessagesRef.current
				if (!el) return
				if (smooth) {
					el.scrollTo({ top: el.scrollHeight, behavior: "smooth" })
				} else {
					el.scrollTop = el.scrollHeight
				}
				setShowScrollBtn(false)
				setFloatingDate(null)
			})
		})
	}, [])

	const handleChatScroll = useCallback(() => {
		const el = chatMessagesRef.current
		if (!el) return

		const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight
		setShowScrollBtn(distFromBottom > 150)

		if (distFromBottom < 80) {
			setFloatingDate(null)
			return
		}

		const containerTop = el.getBoundingClientRect().top
		const separators = Array.from(
			el.querySelectorAll<HTMLElement>("[data-date]")
		)
		let active: string | null = null
		for (const sep of separators) {
			const rect = sep.getBoundingClientRect()
			if (rect.top <= containerTop + 52) active = sep.getAttribute("data-date")
		}
		setFloatingDate(active)
	}, [])

	const resetScroll = useCallback(() => {
		setShowScrollBtn(false)
		setFloatingDate(null)
	}, [])

	return {
		messages,
		loadingMessages,
		showScrollBtn,
		floatingDate,
		chatMessagesRef,
		messagesEndRef,
		scrollToBottom,
		handleChatScroll,
		resetScroll,
	}
}
